import { CollectionState } from '../CollectionState';
import { addEventListener } from '../../util';
import { computed, makeObservable } from 'mobx';
import { compareFilters } from './compareFilters';
import { compareSortArrays } from './compareSortArrays';

export interface SortValue {
  fieldKey: string;
  order: 'asc' | 'desc';
}

export interface Dataset {
  filters: Record<string, {} | undefined>;
  sort: SortValue[];
}

export interface CollectionDatasetSyncStateParams {
  dataset: Dataset;
  onDatasetChange?: (dataset: Dataset) => void;
  collection: CollectionState<any>;
}

export class CollectionDatasetSyncState {
  dataset;
  onDatasetChange;
  collection;

  constructor(params: CollectionDatasetSyncStateParams) {
    this.dataset = params.dataset;
    this.onDatasetChange = params.onDatasetChange;
    this.collection = params.collection;

    this._syncDatasetToCollection();

    makeObservable(this, {
      collectionAsDataset: computed,
      isDeepEqual: computed,
    });
  }

  _syncDatasetToCollection() {
    const { dataset, collection } = this;
    const { query } = collection;

    query.resetFilters();
    Object.entries(dataset.filters).forEach(
      ([filterName, datasetFilterValue]) => {
        const queryFilter = query.filters[filterName];
        if (queryFilter) {
          queryFilter.setValue(queryFilter.decode(datasetFilterValue));
        }
      },
    );

    query.resetSorting();
    if (dataset.sort && dataset.sort.length > 0) {
      query.sort.setSortQuery(
        dataset.sort.map(({ fieldKey, order }) => ({
          field: fieldKey,
          direction: order,
        })),
        { emitChangeEvent: true },
      );
    } else {
      query.resetSorting({ emitChangeEvent: true });
    }
  }

  setDataset(dataset: Dataset) {
    // when props.dataset changes, we need to refresh the collection
    if (dataset === this.dataset) {
      return;
    }

    this.dataset = dataset;

    if (this.isDeepEqual) {
      return;
    }

    this._syncDatasetToCollection();
    this.collection.query.events.emit('refresh');
  }

  get isDeepEqual(): boolean {
    const { collectionAsDataset, dataset, collection } = this;

    // Compare filters
    const filtersEqual = compareFilters(
      collection.query.filters,
      dataset.filters,
      collectionAsDataset.filters,
    );

    if (!filtersEqual) {
      return false;
    }

    // Compare sort arrays
    const sortEqual = compareSortArrays(dataset.sort, collectionAsDataset.sort);
    return sortEqual;
  }

  get collectionAsDataset(): Dataset {
    const { collection } = this;
    const { query } = collection;

    // Extract and encode filters
    const filters: Record<string, {} | undefined> = {};
    query.activeFiltersEntries.forEach(([filterName, filter]) => {
      filters[filterName] = filter.encode(filter.value) as {} | undefined;
    });

    // Convert sort configuration
    const sort: SortValue[] = query.sort.value.map((sortItem) => ({
      fieldKey: sortItem.field,
      order: sortItem.direction,
    }));

    return {
      filters,
      sort,
    };
  }

  init() {
    const { collection } = this;
    return addEventListener(collection.emitter, 'fetch', () => {
      this.onDatasetChange?.(this.collectionAsDataset);
    });
  }
}
